import React from 'react';
import { sourceDisplayName } from '@/utils/sourceDisplayName';
import TagChip, { type TagChipMode } from './TagChip';
import type { StatusVariant } from './StatusBadge';

export type SourceChipVariant = 'compact' | 'full';

export type SourceChipProps = {
  /** Source identifier (e.g. mobile_de). */
  source: string;
  /** When false, chip shows disabled styling. Defaults to true. */
  enabled?: boolean;
  variant?: SourceChipVariant;
  mode?: TagChipMode;
  selected?: boolean;
  disabled?: boolean;
  href?: string;
  onClick?: () => void;
  /** Tooltip; defaults to raw source name. */
  title?: string;
  'aria-label'?: string;
  variantTone?: StatusVariant;
  className?: string;
};

/** Single marketplace source chip — shared styling everywhere. */
export default function SourceChip({
  source,
  enabled = true,
  variant = 'compact',
  mode = 'static',
  selected = false,
  disabled = false,
  href,
  onClick,
  title,
  'aria-label': ariaLabel,
  variantTone,
  className = '',
}: SourceChipProps) {
  const label = sourceDisplayName(source);
  const chipDisabled = disabled || !enabled;
  const sizeClass = variant === 'full' ? 'source-chip--full' : 'source-chip--compact';

  let content: React.ReactNode = label;
  if (!enabled && mode === 'toggle') {
    content = (
      <>
        {label}
        <span className="source-chip__muted"> (off)</span>
      </>
    );
  }

  const resolvedAriaLabel =
    ariaLabel ??
    (!enabled ? `${label} (disabled)` : selected ? `${label} selected` : label);

  return (
    <TagChip
      mode={mode}
      label={content}
      selected={selected}
      disabled={chipDisabled}
      href={href}
      onClick={onClick}
      title={title ?? source}
      aria-label={mode === 'toggle' ? resolvedAriaLabel : undefined}
      variant={variantTone ?? (!enabled ? 'warning' : undefined)}
      className={[sizeClass, className].filter(Boolean).join(' ')}
    />
  );
}
